import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Description, Settings } from '@mui/icons-material';
import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CustomPagination } from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { OrdensContext } from 'contexts/OrdensContext';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { useModal } from 'components/Modals';
import Card from 'components/Card';
import Header from 'components/Header';
import Button from 'components/Button';
import Container from 'components/Container';
import VirtualDrop from 'components/VirtualDrop';
import localeText from 'utils/localeText';
import moment from 'moment';
import styles from './styles';
import ConfigModal from './ConfigModal';

const Necessidade = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { getNecessidade, getLoading, postLoading, postCalculo } =
    useContext(OrdensContext);
  const {
    relatorios,
    getRelatorios,
    getLoading: relatorioLoading,
  } = useContext(RelatoriosContext);
  const { control, setValue, handleSubmit } = useForm();
  const {
    control: searchControl,
    watch: watchSearch,
    reset: resetSearch,
  } = useForm({ defaultValues: { produto: null, origem: null } });
  const [url, setUrl] = useState(null);
  const [rows, setRows] = useState([]);
  const [filtros, setFiltros] = useState([]);
  const [selected, setSelected] = useState([]);
  const loading = getLoading || relatorioLoading;
  const searchProduto = watchSearch('produto');
  const searchReferencia = watchSearch('referencia');
  const searchOrigem = watchSearch('origem');

  const normalize = (value) =>
    String(value || '')
      .trim()
      .toUpperCase();

  const produtoOptions = useMemo(() => {
    const produtos = rows?.reduce((acc, item) => {
      if (
        item?.produto_id == null ||
        acc?.some((p) => p?.value === item?.produto_id)
      ) {
        return acc;
      }

      return [
        ...acc,
        {
          label:
            item?.descricao || item?.referencia || String(item?.produto_id),
          value: item?.produto_id,
          Referência: item?.referencia,
        },
      ];
    }, []);

    return produtos?.sort((a, b) => a?.label?.localeCompare(b?.label));
  }, [rows]);

  const origemOptions = useMemo(() => {
    const origens = rows
      ?.map((item) => item?.origem)
      ?.filter(
        (origem, index, arr) => origem && arr?.indexOf(origem) === index
      );

    return origens?.map((origem) => ({ label: origem, value: origem }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const referencia = normalize(searchReferencia);

    return rows?.filter((row) => {
      const matchProduto =
        !searchProduto || String(row?.produto_id) === String(searchProduto);
      const matchReferencia =
        !referencia || normalize(row?.referencia)?.includes(referencia);
      const matchOrigem =
        !searchOrigem || String(row?.origem) === String(searchOrigem);

      return matchProduto && matchReferencia && matchOrigem;
    });
  }, [rows, searchOrigem, searchProduto, searchReferencia]);

  useEffect(() => {
    const visibleIds = filteredRows?.map((row) => row?.id);
    setSelected((prev) => {
      const next = prev?.filter((id) => visibleIds?.includes(id));
      if (next?.length === prev?.length) return prev;
      return next;
    });
  }, [filteredRows]);

  useEffect(() => {
    getRelatorios();
  }, []);

  const onFilter = (params) => {
    setSelected([]);
    setUrl(null);
    setRows([]);
    resetSearch();

    getNecessidade({
      params,
      cb: (data) => {
        const arr = data?.data || [];
        setUrl(data?.url || null);
        setRows(arr?.map((m, id) => ({ ...m, id, input: m?.quantidade })));
      },
    });
  };

  const openConfigModal = (items = filtros) => {
    openModal(
      <ConfigModal
        control={control}
        drops={drops}
        filtros={items}
        loading={loading}
        onFilter={handleSubmit((params) => {
          closeModal();
          onFilter(params);
        })}
      />,
      90
    );
  };

  useEffect(() => {
    const filtros =
      relatorios?.find((f) => f?.nome === 'Necessidade de produção')?.fields ||
      [];
    setFiltros(filtros);
    filtros.map((item) => {
      switch (item?.type) {
        case 'date':
          setValue(item?.field, null);
          break;
        case 'drop':
          setValue(item?.field, null);
          break;
        default:
          setValue(item?.field, '');
          break;
      }
    });
    openConfigModal(filtros);
  }, [relatorios]);

  const onCellEditCommit = ({ id, field, value }) => {
    setRows((prev) =>
      prev?.map((p) => (p?.id === id ? { ...p, [field]: value } : p))
    );
  };

  const onSubmit = (filtro) => {
    const dtemissao = moment().format('YYYY-MM-DD HH:mm:ss');
    const itens_fabricados = [];
    const itens_comprados = [];
    const documentos = [];

    selected?.map((id) => {
      const item = rows?.find((f) => f?.id === id);
      if (item?.origem === 'FABRICADO') {
        itens_fabricados?.push(item);
      }
      if (item?.origem === 'COMPRADO') {
        itens_comprados?.push(item);
      }
    });

    itens_fabricados?.map((item) => {
      const quantidade = Number(item?.input);
      const ofab = {
        Documento: {
          especie_id: 17,
          natureza_operacao_id: 17,
          users_id: user?.id,
          filial_id: user?.filial_id,
          dtemissao,
        },
        Itens: [
          {
            sequencia: 1,
            quantidade,
            przent: item?.przent,
            descricao: item?.descricao,
            produto_id: item?.produto_id,
            referencia: item?.referencia,
          },
        ],
      };

      if (!quantidade || quantidade <= 0) return;

      documentos?.push(ofab);
    });

    if (itens_comprados?.length > 0) {
      const reqcomp = {
        Documento: {
          especie_id: 2,
          natureza_operacao_id: 1,
          users_id: user?.id,
          filial_id: user?.filial_id,
          dtemissao,
        },
        Entrada: { dtentrada: dtemissao },
        RequisicaoCompra: { users_id: user?.id },
        Itens: itens_comprados
          ?.map((item, index) => {
            const quantidade = Number(item?.input);
            if (!quantidade || quantidade <= 0) return null;
            return {
              quantidade,
              przent: item?.przent,
              sequencia: index + 1,
              descricao: item?.descricao,
              produto_id: item?.produto_id,
              referencia: item?.referencia,
            };
          })
          ?.filter(Boolean),
      };

      if (reqcomp?.Itens?.length > 0) {
        documentos?.push(reqcomp);
      }
    }

    if (!documentos?.length) {
      return toastWarning('Nenhum item com quantidade selecionado');
    }
    const data = { filtro, documentos };

    postCalculo({ data, cb: () => navigate(-1) });
  };

  const columns = [
    {
      field: 'produto',
      headerName: 'Produto',
      flex: 1,
      minWidth: 400,
      sortable: false,
      renderCell: (props) => <ProdutoCell {...props} />,
    },
    {
      field: 'origem',
      headerName: 'Origem',
      width: 150,
      sortable: false,
      renderCell: (props) => <OrigemCell {...props} />,
    },
    {
      field: 'necessidade',
      headerName: 'Necessidade',
      width: 200,
      sortable: false,
      renderCell: (props) => <NecessidadeCell {...props} />,
    },
    {
      field: 'input',
      headerName: 'Reposição',
      width: 200,
      sortable: false,
      editable: true,
      renderCell: (props) => <InputCell {...props} />,
    },
  ];

  const ProdutoCell = ({ row }) => (
    <Box flex={1} my={1}>
      <Typography variant="body1" fontWeight="bold" mb={1}>
        {row?.descricao || ''}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="caption" fontWeight="bold">
          Referência:
        </Typography>
        <Typography variant="caption">{row?.referencia || '-'}</Typography>
      </Box>
    </Box>
  );

  const OrigemCell = ({ row }) => (
    <Box flex={1} display="flex" alignItems="center" justifyContent="center">
      <Chip
        size="small"
        variant="outlined"
        label={row?.origem || '-'}
        color={row?.origem === 'COMPRADO' ? 'info' : 'warning'}
      />
    </Box>
  );

  const NecessidadeCell = ({ row }) => (
    <Box flex={1}>
      <Typography variant="body2" fontWeight="bold">
        {row?.quantidade || '0'} {row?.unidade_medida || ''}
      </Typography>
      <Typography variant="caption">
        {Boolean(row?.dtnec) &&
          `Requisitar até ${moment(row?.dtnec).format('DD/MM/YYYY')}`}
      </Typography>
    </Box>
  );

  const InputCell = ({ row }) => (
    <Typography variant="body2">
      {row?.input || '0'} {row?.unidade_medida || ''}
    </Typography>
  );

  return (
    <Container fillHeight>
      <Header titulo="Necessidade de Produção" />
      <Card fillHeight style={styles?.card}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
          <Box minWidth={240} flex={2}>
            <VirtualDrop
              name="produto"
              label="Produto"
              control={searchControl}
              options={produtoOptions}
            />
          </Box>
          <Box minWidth={180} flex={1}>
            <VirtualDrop
              name="origem"
              label="Origem"
              control={searchControl}
              options={origemOptions}
            />
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            sx={styles?.button}
            onClick={() => resetSearch()}
          >
            Limpar busca
          </Button>
          {Boolean(url) && (
            <Button
              variant="outlined"
              sx={styles?.button}
              startIcon={<Description />}
              onClick={() => window.open(url)}
            >
              Ver relatório
            </Button>
          )}
          <Button
            variant="contained"
            sx={styles?.button}
            startIcon={<Settings />}
            onClick={() => openConfigModal()}
          >
            Calcular
          </Button>
        </Box>
        <DataGrid
          rows={filteredRows}
          pageSize={50}
          columns={columns}
          density="compact"
          sx={styles?.datagrid}
          selectionModel={selected}
          pagination
          disableColumnMenu
          checkboxSelection
          showCellRightBorder
          disableVirtualization
          showColumnRightBorder
          disableSelectionOnClick
          keepNonExistentRowsSelected
          loading={loading}
          localeText={localeText}
          getRowHeight={() => 'auto'}
          onCellEditCommit={onCellEditCommit}
          onSelectionModelChange={(ids) => setSelected(ids)}
          components={{
            LoadingOverlay: LinearProgress,
            Pagination: CustomPagination,
          }}
        />
      </Card>
      <Button
        color="primary"
        onClick={handleSubmit(onSubmit)}
        variant="contained"
        sx={styles?.button}
        loading={postLoading}
      >
        Comprar / Produzir
      </Button>
    </Container>
  );
};

export default Necessidade;
